import React, { useState } from 'react';
import axios from '../config/axiosConfig';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from './ToastProvider';
import { motion } from 'framer-motion';

const emptyForm = () => ({
  ownerName: '',
  shopName: '',
  email: '',
  contactNo: '',
  city: '',
  state: '',
  pincode: '',
});

export default function RestroLiteContactForm({ className = '' }) {
  const addToast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [contactError, setContactError] = useState('');

  const inputClass =
    'w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-stone-50 border border-stone-200 rounded-lg sm:rounded-xl text-stone-900 text-sm placeholder-stone-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors';
  const labelClass = 'block text-xs sm:text-sm font-medium text-stone-700 mb-0.5 sm:mb-1';

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contactNo') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setForm((prev) => ({ ...prev, contactNo: digitsOnly }));
      setContactError(
        digitsOnly.length > 0 && digitsOnly.length < 10 ? 'Enter a 10-digit mobile number' : ''
      );
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const contactDigits = (form.contactNo || '').replace(/\D/g, '');
    if (contactDigits.length !== 10) {
      setContactError('Enter a valid 10-digit mobile number');
      return;
    }
    setContactError('');
    setSubmitting(true);
    try {
      await axios.post(API_ENDPOINTS.RESTRO_LITE_LEADS_SUBMIT, form);
      setSubmitted(true);
      setForm(emptyForm());
      addToast('Thank you! We will contact you soon about PocketBillingAI.', 'success');
    } catch (err) {
      addToast(err.response?.data?.message || 'Failed to submit. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-white rounded-xl sm:rounded-2xl p-8 sm:p-10 border border-stone-200/80 shadow-lg text-center ${className}`}
      >
        <div className="text-5xl mb-4" aria-hidden>
          ✓
        </div>
        <h2 className="text-2xl font-bold text-stone-900 mb-2">Thank you!</h2>
        <p className="text-stone-600 mb-6">
          We have received your details for <strong>PocketBillingAI</strong>. Our team will contact you soon
          to set up your shop.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          Submit another enquiry
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-stone-200/80 shadow-sm ${className}`}
    >
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div>
          <label className={labelClass} htmlFor="lite-ownerName">
            Owner name *
          </label>
          <input
            id="lite-ownerName"
            type="text"
            name="ownerName"
            value={form.ownerName}
            onChange={handleChange}
            required
            className={inputClass}
            placeholder="Your name"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="lite-shopName">
            Shop name *
          </label>
          <input
            id="lite-shopName"
            type="text"
            name="shopName"
            value={form.shopName}
            onChange={handleChange}
            required
            className={inputClass}
            placeholder="Your shop / kirana / store name"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="lite-email">
            Email
          </label>
          <input
            id="lite-email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className={inputClass}
            placeholder="you@example.com (optional)"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="lite-contactNo">
            Contact number *
          </label>
          <input
            id="lite-contactNo"
            type="tel"
            name="contactNo"
            value={form.contactNo}
            onChange={handleChange}
            required
            maxLength={10}
            minLength={10}
            inputMode="numeric"
            pattern="[6-9][0-9]{9}"
            className={`${inputClass} ${contactError ? 'border-red-400 focus:ring-red-500' : ''}`}
            placeholder="10-digit mobile number"
          />
          {contactError && <p className="text-red-600 text-xs mt-1">{contactError}</p>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="sm:col-span-2">
            <label className={labelClass} htmlFor="lite-city">
              City *
            </label>
            <input
              id="lite-city"
              type="text"
              name="city"
              value={form.city}
              onChange={handleChange}
              required
              className={inputClass}
              placeholder="City"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="lite-state">
              State *
            </label>
            <input
              id="lite-state"
              type="text"
              name="state"
              value={form.state}
              onChange={handleChange}
              required
              className={inputClass}
              placeholder="State"
            />
          </div>
        </div>
        <div>
          <label className={labelClass} htmlFor="lite-pincode">
            Pincode
          </label>
          <input
            id="lite-pincode"
            type="text"
            name="pincode"
            value={form.pincode}
            onChange={handleChange}
            maxLength={10}
            className={inputClass}
            placeholder="Pincode (optional)"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 px-4 sm:py-3 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all shadow-lg shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed mt-4 sm:mt-6"
        >
          {submitting ? 'Submitting…' : 'Submit — Start PocketBillingAI trial'}
        </button>
      </form>
    </motion.div>
  );
}
